import axios, { retryOnTimeout } from "../../axios";
import { getQuery } from "./dataSettings/dataSettings";
import * as actionTypes from "./actionTypes";
import {
  UPDATE_ACTIVE_TABLE_ROW_BY_WEBSOCKET,
  BULK_UPDATE_ACTIVE_TABLE_ROW_BY_WEBSOCKET,
  INSERT_ACTIVE_TABLE_ROW_LOCALLY,
} from "./actionTypes";
import { getPages } from "./pageManagement";
import { loadDashboardConfiguration } from "./dashboard/dashboard";
import { defaultConnectionSelector } from "./connections";
import prepareSettings from "./dataSettings/prepareSettings";
import convertFiltersToOperatorsMode, {
  convertFiltersToConfigFormat,
  convertOverridesToConfigFormat,
} from "../../Editors/BlockEdit/VisualizationGuiEditSection/convertFiltersToOperatorsMode";
import { convertVisibleFieldFormToRequest } from "../../utils/activeTable/viewVisibleFieldsFormatConverter";
import {
  convertJsonDataToMultipartFormDataAndJson,
  hasFileRecursively,
} from "../../utils/files";
import {
  handleError,
  normalizeError,
  NOT_FOUND,
  UserFacingError,
} from "../../utils/errorHandling";
import { wrapAsyncThunk } from "../../utils/apiResultWrapper";
import { isObject, omit } from "lodash-es";
import { showToastWithTimeout } from "./message";
import {
  HEADER_MERGE_JSON_DATA,
  HEADER_CONVERT_EMPTY_STRINGS_TO_NULL,
} from "../../utils/axios/common";
import { printQueryHtmlTemplateForm } from "./htmlTemplates";
import { systemColumns } from "../../charts/ActiveTable/useActiveTableSettings";
import { parse } from "qs";

export const listActiveTables = () => {
  return {
    type: actionTypes.LOAD_ACTIVE_TABLES_START,
    meta: {
      api: {
        endpoint: "/api/v1/active-tables",
        method: "GET",
      },
    },
  };
};

let getActiveTableAbortController = new AbortController();

export function getActiveTable(uuid, queryId, abortController) {
  return async (dispatch) => {
    getActiveTableAbortController.abort();
    getActiveTableAbortController = abortController ?? new AbortController();
    const signal = getActiveTableAbortController.signal;

    dispatch({ type: actionTypes.GET_ACTIVE_TABLE_START });
    const [activeTableResponse, queryResponse] = await Promise.all([
      axios.get(`/api/v1/active-tables/${uuid}`, { signal }),
      axios.get(`/api/v1/queries/${queryId}`, { signal }),
    ]);

    const queryResponseData = queryResponse.data.data;

    dispatch({
      type: actionTypes.GET_ACTIVE_TABLE_SUCCESS,
      activeTable: activeTableResponse.data.data,
      query: queryResponseData,
      dataSourceUuid: queryResponseData.dataSources[0].uuid,
    });
  };
}

/**
 * Accepts a list of menu filters with their values (checked or not),
 * and returns only the checked values, and for only those menu filters that
 * should be applied on the current page.
 *
 * @param getState
 * @param menuFilters
 * @returns {*[]}
 */
export function getMenuFiltersOnPage(getState, menuFilters = []) {
  const { activeTab } = getState().layout;

  return menuFilters
    .filter(({ show, hide }) => {
      if (show) {
        return (show ?? []).includes(activeTab?.uuid);
      }

      return !(hide ?? []).includes(activeTab?.uuid);
    })
    .flatMap(({ values }) => (values ?? []).filter((v) => v.checked));
}

let activeTableAbortController = new AbortController();

export const preparePrintFormSettings =
  (htmlTemplateConfig, view, menuFilters) => (dispatch, getState) => {
    const {
      templateName,
      templateQueryId,
      templateUuid,
      templateOverrides,
      templateLimit,
      ...rest
    } = htmlTemplateConfig;

    const preparedSettings = getActiveTableRowsPreparedSettings(
      getState,
      templateQueryId,
      view,
      menuFilters,
      "chart", // format
      null, // splitByFilter,
      null, // exportMode,
      true
    );

    // connect with data explorer filters and overrides (aggregations and etc.)
    const { filters, overrides } = preparedSettings ?? {};
    const configFilters = convertFiltersToConfigFormat(filters ?? []);
    const configOverrides = convertOverridesToConfigFormat(
      overrides?.fields ?? []
    );

    dispatch(
      printQueryHtmlTemplateForm({
        queryUuid: templateQueryId,
        templateName,
        chart: {
          filters: configFilters,
          overrides: templateOverrides ?? configOverrides,
          limit: templateLimit ?? 50,
          // template override
          export: {
            template: templateUuid
              ? { uuid: templateUuid }
              : { name: templateName },
          },
          ...rest, // looks like html template configuration can include parameterized settings,
        },
        templateId: "active-table-loading",
      })
    );
  };

export const prepareExportFromSettings =
  (queryUuid, view, menuFilters, splitByFilter) => (dispatch, getState) => {
    const preparedSettings = getActiveTableRowsPreparedSettings(
      getState,
      queryUuid,
      view,
      menuFilters,
      "csv",
      splitByFilter,
      true // do not include joined column
    );

    function getExportFields() {
      if (!view) {
        const { currentTable } = getState().activeTable ?? {};

        return currentTable?.columns?.map((col) => ({
          name: col.name,
          ...(col.type === "api-image" && {
            mapping: { type: "api-image" },
          }),
        }));
      }

      return preparedSettings.overrides?.fields || [];
    }

    function determineExportType(fields) {
      const hasApiImages = fields.some(
        (field) => field.mapping?.type === "api-image"
      );
      return hasApiImages ? "fancy-excel" : preparedSettings.format;
    }

    function buildOverrides(fields) {
      return {
        fields: fields.filter((field) => !systemColumns.includes(field.name)),
      };
    }

    function buildExportPayload(overrides, format) {
      return {
        ...omit(preparedSettings, ["limit", "id"]),
        overrides,
        format,
      };
    }

    const fields = getExportFields();
    const exportType = determineExportType(fields);
    const overrides = buildOverrides(fields);
    const payload = buildExportPayload(overrides, exportType);
    const fileName = view?.name ?? "Active Table";

    dispatch({
      type: actionTypes.EXPORT_ACTIVE_TABLE_START,
      meta: {
        api: {
          method: "POST",
          endpoint: `/api/v1/queries/${queryUuid}/exec`,
          payload,
          format: exportType,
          file: true,
          fileName,
        },
      },
      splitByKey: splitByFilter?.value,
    });
  };

export const loadActiveTableData =
  (queryUuid, view, menuFilters = [], chart, abortController, options) =>
  (dispatch, getState) => {
    if (!getState().activeTable.currentTable) {
      // This check avoids a console error when navigating away.
      return;
    }

    options ??= {};
    const preparedSettings = getActiveTableRowsPreparedSettings(
      getState,
      queryUuid,
      view,
      menuFilters
    );

    // separate limit for each view
    const viewLimit = view?.displaySettings?.limit;

    const location = window.location;
    const queryParams = parse(location.search, { ignoreQueryPrefix: true });

    const payload = {
      ...preparedSettings,
      perPage: viewLimit || chart?.limit || 1000,
      page: queryParams.activeTablePage,
    };

    if (options.search) {
      payload.search = options.search;
      delete payload.page;
    }
    if (options.orderBy?.length) {
      payload.orderBy = [...options.orderBy, ...(payload.orderBy ?? [])];
      delete payload.order;
    }

    // Abort any previous request.
    activeTableAbortController.abort();

    activeTableAbortController = abortController ?? new AbortController();

    return dispatch({
      type: actionTypes.GET_ACTIVE_TABLE_DATA_START,
      meta: {
        api: {
          method: "POST",
          endpoint: `/api/v1/queries/${queryUuid}/exec`,
          headers: {
            [HEADER_CONVERT_EMPTY_STRINGS_TO_NULL]: "0",
          },
          signal: activeTableAbortController.signal,
          payload,
        },
      },
    });
  };

export function getActiveTableRowsPreparedSettings(
  getState,
  queryUuid,
  view,
  menuFilters = [],
  format = "chart",
  splitByFilter,
  exportMode,
  // viewFiltersOnly = true need to use only view filters (not left menu filter)
  // because for print html template we using query builder which already have all left menu filters
  viewFiltersOnly = false
) {
  const configFilters = convertFiltersToConfigFormat(view?.filters || []);

  const existingCheckedMenuFilters = getMenuFiltersOnPage(
    getState,
    menuFilters
  );

  const allFilters = viewFiltersOnly
    ? configFilters
    : [...configFilters, ...existingCheckedMenuFilters];

  // need for export if spitby exist
  if (splitByFilter) {
    allFilters.push(splitByFilter);
  }

  const filters = convertFiltersToOperatorsMode(allFilters, {
    noOperatorFallback: true,
  });

  const query = {
    format,
    limit: 100,
    id: queryUuid,
  };

  let wasAggregationUsed = false;

  if (view) {
    const overrideFields = [];
    const fieldNamesUsed = {};
    const activeTable = getState().activeTable;
    for (const visibleField of view.visibleFields) {
      const column = activeTable.currentTable.columns.find(
        (c) => c.uuid === visibleField.colId
      );

      const name =
        column?.name ??
        activeTable.queryFields.find(
          (c) => c.dataSourceFieldUuid === visibleField.colId
        )?.name;

      if (!name) {
        continue;
      }

      const overrideField = { name };

      if (column?.type === "api-image") {
        overrideField.mapping = { type: "api-image" };
      }

      if (visibleField.aggregationType) {
        wasAggregationUsed = true;
        overrideField.aggregation = { type: visibleField.aggregationType };
        overrideField.alias = overrideField.name;
      }
      overrideFields.push(overrideField);
      fieldNamesUsed[name] = true;
    }
    if (!wasAggregationUsed) {
      if (!fieldNamesUsed.uuid) {
        overrideFields.push({ name: "uuid" });
      }
      const columnToJoinName =
        activeTable.currentTable.joinedMode?.columnToJoinName;
      if (
        columnToJoinName &&
        !fieldNamesUsed[columnToJoinName] &&
        !exportMode
      ) {
        overrideFields.push({ name: columnToJoinName });
      }
    }
    if (view.displaySettings?.orders?.length) {
      query.orderBy = view?.displaySettings.orders
        .filter((v) => fieldNamesUsed[v.name])
        .map((v) => ({
          name: v.name,
          direction: v.sort === "CUSTOM" ? "ASC" : v.sort,
          values: v.values?.map((v) => String(v)) ?? undefined,
        }));
    }
    query.overrides = { fields: overrideFields };
  }
  if (!wasAggregationUsed && !query.orderBy) {
    query.order = {
      io_created_at: "DESC",
    };
  }

  return prepareSettings({
    ...query,
    filters,
  });
}

function locateVisualizationUuid(visualizationUuid, getState) {
  const { activeTab } = getState().layout;

  const block = (activeTab?.blocks ?? []).find((b) => {
    if (visualizationUuid) {
      return b.visualizations.find((v) => v.uuid === visualizationUuid);
    } else {
      return b.visualizations.find((v) => v.settings.type === "ActiveTable");
    }
  });

  const pageUuid = activeTab?.uuid;
  const blockUuid = block?.uuid;

  return { pageUuid, blockUuid };
}

export const activeTableVisPayload = {
  filters: [],
  overrides: [],
  orders: [],
  settings: {
    type: "ActiveTable",
  },
};
export const initializeActiveTable = (page, block) => async (dispatch) => {
  // check if visualization already exists
  const nextPayload = {
    ...block,
    visualizations: [...block.visualizations, activeTableVisPayload],
  };

  await dispatch({
    type: actionTypes.INITIALIZE_ACTIVE_TABLE_START,
    pageUuid: page.uuid,
    meta: {
      api: {
        method: "PUT",
        endpoint: `/api/v1/pages/${page.uuid}/blocks/${block.uuid}`,
        payload: nextPayload,
      },
    },
  });
  dispatch(getPages());
};

// This is the initial column in a table, when this is new, there will be
// no values, so at least one needs to be editable
const defaultColumn = {
  name: "start_here",
  type: "string",
  defaultValue: null,
  valueOptions: {}, // Select options list
  displaySettings: { activeOnly: true },
  validationRules: {},
  isNullable: true,
  isEditable: true,
};

export const createNewActiveTable =
  (tableName, visualizationUuid, pageSlug, createType, joinedMode) =>
  async (dispatch, getState) => {
    const defaultConnection = defaultConnectionSelector(getState());
    const { pageUuid, blockUuid } = locateVisualizationUuid(
      visualizationUuid,
      getState
    );

    const isJoinType = createType !== "new" && joinedMode.mode;

    let createTableResponse;

    const payload = {
      connectionUuid: defaultConnection.uuid,
      dbName: defaultConnection.database,
      tbl: tableName, // existing or new source table
      fromExistingTable: createType === "existing",
      columns: [defaultColumn],
    };

    if (createType !== "new") {
      const {
        mode,
        viewName,
        activeTableName,
        columnToJoinName,
        columnToJoinType,
        namespace,
      } = joinedMode;

      // Join Mode
      if (mode) {
        payload.fromExistingTable = false;
        payload.tbl = activeTableName; // Active Table Table Name
        payload.joinedMode = {
          viewName, // Output View
          columnToJoinName, // Foreign Key
          tableToJoinName: (namespace ? namespace + "." : "") + tableName,
        };
        payload.columns = [
          defaultColumn,
          {
            isNullable: false,
            isEditable: false,
            name: columnToJoinName,
            type: columnToJoinType,
          },
        ];
      }
    }

    try {
      createTableResponse = await dispatch({
        type: actionTypes.CREATE_NEW_ACTIVE_TABLE_START,
        meta: {
          api: {
            endpoint: `/api/v1/active-tables`,
            method: "POST",
            toastOnFailure: true,
            payload,
          },
        },
      });
      if (createTableResponse.error) {
        return dispatch({
          type: actionTypes.CREATE_NEW_ACTIVE_TABLE_FAIL,
          error: createTableResponse.error,
        });
      } else {
        dispatch({
          type: actionTypes.CREATE_NEW_ACTIVE_TABLE_SUCCESS,
          results: createTableResponse.payload.data,
        });
      }
    } catch (e) {
      return dispatch({
        type: actionTypes.CREATE_NEW_ACTIVE_TABLE_FAIL,
        error: e,
      });
    }

    const queryTable = isJoinType ? joinedMode.viewName : tableName;

    const queryResponse = await createQueryFromTable(
      queryTable,
      defaultConnection.uuid,
      defaultConnection.database
    );

    dispatch({
      type: actionTypes.UPDATE_ACTIVE_TABLE_CHART_CONFIG_START,
    });

    await axios.request({
      method: "PUT",
      url: `api/v1/pages/${pageUuid}/blocks/${blockUuid}/visualizations/${visualizationUuid}`,
      data: {
        queryUuid: queryResponse.uuid,
        filters: null,
        overrides: null,
        orders: null,
        settings: {
          type: "ActiveTable",
          activeTableUuid: createTableResponse.payload.data.data.uuid,
        },
      },
    });
    dispatch({ type: actionTypes.CREATE_NEW_ACTIVE_TABLE_PROCESS_COMPLETE });
    dispatch(loadDashboardConfiguration(pageSlug));
  };

// Move below to dataSettings
export const createQueryFromTable = async (
  tableName,
  connectionUuid,
  database
) => {
  const displayName = `Active Table - ${tableName}`;
  // create DataSource
  const dataSourcePayload = {
    typeId: 3, // force to type 3, database type
    connectionUuid,
    displayName,
    db: database,
    tbl: tableName,
  };

  const dataSourceResponse = await axios.post(
    "/api/v1/data_sources",
    dataSourcePayload
  );
  const queryPayload = {
    name: displayName,
    dataSources: [dataSourceResponse.data.data],
  };
  const queryResponse = await axios.post("/api/v1/queries", queryPayload);
  return queryResponse.data.data;
};

// api do not support dates with time like: 2024-08-28 12:56:25
// here we drop time part
function convertIfDate(columns, name, value) {
  if (value === "None...") {
    return null;
  }

  const isDate = columns.some(
    (column) => column.name === name && column.type === "date"
  );

  if (isDate) {
    return (value ?? "").slice(0, 10);
  }

  return value;
}

export function insertNewRow(rowData) {
  return {
    type: INSERT_ACTIVE_TABLE_ROW_LOCALLY,
    payload: rowData,
  };
}

export const createRow = (tableUuid, payload, columns, key) => {
  let body = columns.reduce((acc, curr) => {
    return {
      ...acc,
      [curr.name]: convertIfDate(columns, curr.name, payload[curr.name]),
    };
  }, {});
  const headers = {};
  const hasFile = hasFileRecursively(body);
  if (hasFile) {
    body = convertJsonDataToMultipartFormDataAndJson(body);
    headers["Content-Type"] = "multipart/form-data";
    headers[HEADER_MERGE_JSON_DATA] = "1";
  }

  return {
    type: actionTypes.ADD_ACTIVE_TABLE_ROW_START,
    meta: {
      api: {
        endpoint: `/api/v1/active-tables/${tableUuid}/rows`,
        method: "POST",
        payload: body,
        headers,
      },
    },
    key,
    value: payload[key],
  };
};

export const updateRow =
  (tableUuid, payload, columns, changedFieldName) => (dispatch) => {
    // Create a new FormData instance
    dispatch({ type: actionTypes.UPDATE_ACTIVE_TABLE_ROW_START });

    const { io_created_at, io_updated_at, ...updatePayload } = payload;

    // update only changed column, not all of them (uuid of row is required)
    // reason: there can be fields which cant be modified
    const directPayload = {
      uuid: updatePayload.uuid,
      [changedFieldName]: convertIfDate(
        columns,
        changedFieldName,
        updatePayload[changedFieldName]
      ),
    };
    // if not a file being updated, send this as a regular PUT with this payload
    const isFileField = updatePayload[changedFieldName] instanceof File;

    if (!isFileField) {
      return axios
        .put(`/api/v1/active-tables/${tableUuid}/rows`, directPayload)
        .then((response) => {
          dispatch({
            type: actionTypes.UPDATE_ACTIVE_TABLE_ROW_SUCCESS,
            results: response.data,
            changedFieldName,
            uniqueRowUuid: updatePayload.uniqueRowUuid,
          });
        })
        .catch((error) => {
          const errorObject = error.response?.data;
          dispatch({
            type: actionTypes.UPDATE_ACTIVE_TABLE_ROW_FAIL,
            error: errorObject,
          });
          throw error;
        });
    } else {
      // Use formData only for file field
      const formData = convertJsonDataToMultipartFormDataAndJson(directPayload);
      formData.append("_method", "PUT");

      return axios
        .post(`/api/v1/active-tables/${tableUuid}/rows`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            [HEADER_MERGE_JSON_DATA]: "1",
          },
        })
        .then((response) => {
          dispatch({
            type: actionTypes.UPDATE_ACTIVE_TABLE_ROW_SUCCESS,
            results: response.data,
            changedFieldName,
            uniqueRowUuid: updatePayload.uniqueRowUuid,
          });
        })
        .catch((error) => {
          const errorObject = error.response?.data;
          dispatch({
            type: actionTypes.UPDATE_ACTIVE_TABLE_ROW_FAIL,
            error: errorObject,
          });
          throw error;
        });
    }
  };

export function deleteRow(activeTableUuid, uniqueRowUuid) {
  if (!uniqueRowUuid) {
    throw new Error("No unique row uuid given.");
  }

  return wrapAsyncThunk(async (dispatch, getState) => {
    const data = getState().activeTable.data;
    let rowUuid;
    let rowFound = false;
    for (const datum of data) {
      if (datum.uniqueRowUuid === uniqueRowUuid) {
        rowUuid = datum.uuid;
        rowFound = true;
        break;
      }
    }

    if (!rowFound) {
      throw new Error("Could not find row.");
    }

    const payload = {
      uniqueRowUuid,
      rowUuid,
    };

    if (!rowUuid) {
      // This row hasn't even been saved yet.
      dispatch({
        type: actionTypes.DELETE_ACTIVE_TABLE_ROW_SUCCESS,
        payload,
      });
      return;
    }

    dispatch({
      type: actionTypes.DELETE_ACTIVE_TABLE_ROW_START,
      payload,
    });

    await axios.delete(`/api/v1/active-tables/${activeTableUuid}/rows`, {
      data: {
        uuid: rowUuid,
      },
    });

    try {
      await verifyDelete(activeTableUuid, rowUuid);
      dispatch({
        type: actionTypes.DELETE_ACTIVE_TABLE_ROW_SUCCESS,
        payload,
      });
      showToastWithTimeout("Row has been deleted successfully.", "success");
    } catch (e) {
      dispatch({
        type: actionTypes.DELETE_ACTIVE_TABLE_ROW_FAIL,
        payload,
        error: e,
      });
      throw e;
    }
  });
}

async function verifyDelete(activeTableUuid, rowUuid) {
  try {
    const response = await axios.get(
      `/api/v1/active-tables/${activeTableUuid}/rows/${rowUuid}`
    );
    const data = response.data.data;
    if (
      isObject(data) &&
      !Object.values(data).filter((v) => v !== null).length
    ) {
      // Row confirmed to have been deleted, because all values for the row
      // show up as NULL.
      return;
    }
  } catch (e) {
    const normalizedError = normalizeError(e);
    if (normalizedError.type === NOT_FOUND) {
      // Row confirmed to have been deleted.
      return;
    }

    throw e;
  }

  throw new UserFacingError("Deletion could not be verified.");
}

export function reportUpdatedRowViaWebsocket(
  rowUuid,
  getState,
  queryUuid,
  view,
  menuFilters,
  signal
) {
  return async (dispatch) => {
    const preparedSettings = getActiveTableRowsPreparedSettings(
      getState,
      queryUuid,
      view,
      menuFilters
    );
    if (!preparedSettings.filters) {
      preparedSettings.filters = [];
    }
    preparedSettings.filters.push({ name: "uuid", values: [rowUuid] });
    const response = await axios.post(
      `/api/v1/queries/${queryUuid}/exec`,
      preparedSettings,
      {
        headers: {
          [HEADER_CONVERT_EMPTY_STRINGS_TO_NULL]: "0",
        },
        signal,
      }
    );

    const rows = response.data.data;
    dispatch({
      type: UPDATE_ACTIVE_TABLE_ROW_BY_WEBSOCKET,
      payload: rows,
    });
  };
}

export function reportBulkUpdateViaWebsocket(data) {
  return {
    type: BULK_UPDATE_ACTIVE_TABLE_ROW_BY_WEBSOCKET,
    payload: data,
  };
}

export function updateActiveTableConfig(
  queryUuid,
  nextTableConfig,
  checkedMenuFilters,
  setRescanPendingMessage,
  search
) {
  return async (dispatch) => {
    try {
      const { activeView, ...payload } = nextTableConfig;
      dispatch({ type: actionTypes.UPDATE_ACTIVE_TABLE_START });

      const activeTableResponse = await axios.put(
        `api/v1/active-tables/${payload.uuid}`,
        payload
      );
      const responseData = activeTableResponse.data.data;
      if (!responseData.hasChanged) {
        return dispatch({
          type: actionTypes.UPDATE_ACTIVE_TABLE_SUCCESS,
          results: activeTableResponse.data,
        });
      }

      const queryResponse = await dispatch(getQuery(queryUuid));
      const query = queryResponse.payload.data.data;
      const queryDataSourceUuid = query.dataSources[0].uuid;

      // show modal with notification on request time more then 10 seconds
      let timer;
      timer = setTimeout(() => {
        setRescanPendingMessage(
          "Your database is being updated, do not close this window."
        );
      }, 10000);

      // Rescan data source updates datasource
      await retryOnTimeout(
        axios.get(
          `api/v1/data_sources/${queryDataSourceUuid}/rescan?writeMode=all`
        ),
        () => setRescanPendingMessage("Request is timing out, retrying...")
      );

      // clear timer and remove notification after rescan success
      clearTimeout(timer);
      setRescanPendingMessage("");

      const ds = await axios.get(`api/v1/data_sources/${queryDataSourceUuid}`);

      const nextQueryPayload = {
        ...query,
        dataSources: [ds.data.data],
        // fields: [], // what are fields for?
      }; // We MIGHT want to have query fields added here precisely later

      // Update query based on data source rescan, because it's not automatic.
      const nextQueryResponse = await axios.put(
        `api/v1/queries/${queryUuid}/`,
        nextQueryPayload
      );

      dispatch({
        type: actionTypes.UPDATE_ACTIVE_TABLE_COLUMN_SUCCESS,
        activeTable: activeTableResponse,
        query: nextQueryResponse,
      });

      await dispatch(
        loadActiveTableData(
          queryUuid,
          activeView,
          checkedMenuFilters,
          undefined,
          undefined,
          { search }
        )
      );
    } catch (error) {
      handleError(error);
      dispatch({ type: actionTypes.UPDATE_ACTIVE_TABLE_FAIL, error });
    }
  };
}

export const saveActiveTableView =
  (activeTableUuid, nextViewConfig, views, reloadData) =>
  async (dispatch, getState) => {
    const viewUuid = nextViewConfig.uuid;
    const method = viewUuid ? "PUT" : "POST";

    const owner = {
      uuid: getState().auth.uuid,
      email: getState().auth.email,
      role: getState().auth.role,
      name: getState().auth.name,
      ttg: getState().auth.ttg,
    };

    // Create or update
    const startType = !!viewUuid
      ? actionTypes.UPDATE_AT_VIEW_START
      : actionTypes.SAVE_AT_VIEW_START;

    const remainingViews = (views ?? []).filter(
      (view) => view.uuid !== nextViewConfig.uuid
    );

    // when isDefaultView is set to some view, need to drop isDefaultView from other views
    if (nextViewConfig?.displaySettings?.isDefaultView) {
      await Promise.all(
        remainingViews.map((view) =>
          axios.put(
            `/api/v1/active-tables/${activeTableUuid}/views/${view.uuid}`,
            {
              ...view,
              ...(view.displaySettings && {
                displaySettings: {
                  ...view.displaySettings,
                  isDefaultView: false,
                },
              }),
              visibleFields: view.visibleFields.map(
                convertVisibleFieldFormToRequest
              ),
            }
          )
        )
      );
    }

    const displaySettings = { ...(nextViewConfig.displaySettings ?? {}) };

    if (!viewUuid) {
      displaySettings.owner = owner;
    }

    const validOrders = displaySettings.orders?.filter((v) => v.name && v.sort);
    await dispatch({
      type: startType,
      meta: {
        api: {
          endpoint: `/api/v1/active-tables/${activeTableUuid}/views/${
            viewUuid ?? ""
          }`,
          method,
          payload: {
            ...nextViewConfig,
            ...(displaySettings && {
              displaySettings: {
                ...displaySettings,
                orders: 1 <= validOrders?.length ? validOrders : undefined,
              },
            }),
            visibleFields: nextViewConfig.visibleFields.map(
              convertVisibleFieldFormToRequest
            ),
          },
        },
      },
    }).unwrap();

    reloadData && reloadData(viewUuid, { nextViewConfig });
  };

export const deleteActiveTableView =
  (activeTableUuid, viewUuid, queryId) => async (dispatch) => {
    await dispatch({
      type: actionTypes.DELETE_AT_VIEW_START,
      meta: {
        api: {
          endpoint: `/api/v1/active-tables/${activeTableUuid}/views/${viewUuid}`,
          method: "DELETE",
          toastOnFailure: true,
        },
      },
    }).unwrap();

    return dispatch(getActiveTable(activeTableUuid, queryId));
  };

export const clearActiveTable = () => (dispatch) => {
  dispatch({ type: actionTypes.CLEAR_ACTIVE_TABLE });
};

export const loadActiveTableViewActivity =
  (activeTableUuid, viewUuid) => async (dispatch) => {
    await dispatch({
      type: actionTypes.GET_ACTIVE_TABLE_VIEW_ACTIVITY_START,
      meta: {
        api: {
          endpoint: `/api/v1/active-tables/${activeTableUuid}/views/${viewUuid}/activity`,
          method: "GET",
          toastOnFailure: true,
        },
      },
    });
  };
